// src/components/paya/PayaReviewPanel.js - Paya Transaction Review Panel
import React, { useState, useMemo } from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Tab,
  Tabs,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  TablePagination,
  Snackbar,
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useEnvironment } from "../../context/EnvironmentContext";
import apiClient from "../../services/EnhancedApiClient";

// ═══════════════════════════════════════════════════════════════
// CSV PARSING
// ═══════════════════════════════════════════════════════════════

function parseCSV(text) {
  const rows = [];
  let current = "";
  let inQuotes = false;
  const lines = [];

  // Split into lines handling quoted newlines
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      current += ch;
    } else if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (current.length > 0) lines.push(current);
      current = "";
      if (ch === "\r" && text[i + 1] === "\n") i++;
    } else {
      current += ch;
    }
  }
  if (current.length > 0) lines.push(current);

  for (const line of lines) {
    const fields = [];
    let field = "";
    let quoted = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (quoted && line[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = !quoted;
        }
      } else if (ch === "," && !quoted) {
        fields.push(field.trim());
        field = "";
      } else {
        field += ch;
      }
    }
    fields.push(field.trim());
    rows.push(fields);
  }

  if (rows.length === 0) return [];
  const headers = rows[0];
  return rows.slice(1).map((fields) => {
    const obj = {};
    headers.forEach((h, i) => {
      obj[h] = fields[i] || "";
    });
    return obj;
  });
}

// ═══════════════════════════════════════════════════════════════
// NON-APPROVED MESSAGE PREFIXES
// ═══════════════════════════════════════════════════════════════

const NON_APPROVED_PREFIXES = [
  "DECLINE",
  "SUSPECTED FRAUD",
  "CARD NO. ERROR",
  "NO ACCOUNT",
  "HOLD-CALL",
  "SERV NOT ALLOWED",
  "EXC APPR AMT LIM",
  "RETRY",
];

function isNonApproved(row) {
  const msg = (row.Message || "").toUpperCase().trim();
  return NON_APPROVED_PREFIXES.some((p) => msg.startsWith(p));
}

// ═══════════════════════════════════════════════════════════════
// UNION-FIND
// ═══════════════════════════════════════════════════════════════

class UnionFind {
  constructor(n) {
    this.parent = Array.from({ length: n }, (_, i) => i);
    this.rank = new Array(n).fill(0);
  }
  find(x) {
    if (this.parent[x] !== x) this.parent[x] = this.find(this.parent[x]);
    return this.parent[x];
  }
  union(a, b) {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra === rb) return;
    if (this.rank[ra] < this.rank[rb]) {
      this.parent[ra] = rb;
    } else if (this.rank[ra] > this.rank[rb]) {
      this.parent[rb] = ra;
    } else {
      this.parent[rb] = ra;
      this.rank[ra]++;
    }
  }
}

// ═══════════════════════════════════════════════════════════════
// CLUSTERING LOGIC
// ═══════════════════════════════════════════════════════════════

function buildFlaggedClusters(rows) {
  // Union-Find: two rows cluster if they share >= 2 of 4 fields
  const uf = new UnionFind(rows.length);
  const fields = ["Bill To", "Billing Address", "Email Address", "SubTotal"];

  for (let i = 0; i < rows.length; i++) {
    for (let j = i + 1; j < rows.length; j++) {
      let matches = 0;
      for (const f of fields) {
        const a = (rows[i][f] || "").toLowerCase().trim();
        const b = (rows[j][f] || "").toLowerCase().trim();
        if (a && b && a === b) matches++;
      }
      if (matches >= 2) uf.union(i, j);
    }
  }

  // Group by root
  const groups = {};
  rows.forEach((_, i) => {
    const root = uf.find(i);
    if (!groups[root]) groups[root] = [];
    groups[root].push(i);
  });

  // Keep clusters where at least one row is non-approved
  const flaggedIndices = new Set();
  const clusters = [];
  for (const indices of Object.values(groups)) {
    if (indices.length < 2) continue; // single-row groups aren't clusters
    const hasNonApproved = indices.some((i) => isNonApproved(rows[i]));
    if (hasNonApproved) {
      clusters.push(indices.map((i) => rows[i]));
      indices.forEach((i) => flaggedIndices.add(i));
    }
  }

  return { clusters, flaggedIndices };
}

function buildDuplicateClusters(rows, excludeIndices) {
  // From rows NOT in flagged clusters, group by any 1 shared field
  const eligible = [];
  const eligibleOrigIdx = [];
  rows.forEach((row, i) => {
    if (!excludeIndices.has(i)) {
      eligible.push(row);
      eligibleOrigIdx.push(i);
    }
  });

  const uf = new UnionFind(eligible.length);
  const fields = ["Bill To", "Billing Address", "Telephone", "Email Address"];

  for (let i = 0; i < eligible.length; i++) {
    for (let j = i + 1; j < eligible.length; j++) {
      let matched = false;
      for (const f of fields) {
        const a = (eligible[i][f] || "").toLowerCase().trim();
        const b = (eligible[j][f] || "").toLowerCase().trim();
        if (a && b && a === b) {
          matched = true;
          break;
        }
      }
      if (matched) uf.union(i, j);
    }
  }

  const groups = {};
  eligible.forEach((_, i) => {
    const root = uf.find(i);
    if (!groups[root]) groups[root] = [];
    groups[root].push(i);
  });

  const dupIndices = new Set();
  const clusters = [];
  for (const indices of Object.values(groups)) {
    if (indices.length < 2) continue;
    clusters.push(indices.map((i) => eligible[i]));
    indices.forEach((i) => dupIndices.add(eligibleOrigIdx[i]));
  }

  return { clusters, dupIndices };
}

// ═══════════════════════════════════════════════════════════════
// TABLE COLUMNS
// ═══════════════════════════════════════════════════════════════

const DISPLAY_COLUMNS = [
  { key: "Date", label: "Date", width: 90 },
  { key: "OrderID", label: "OrderID", width: 110 },
  { key: "SubTotal", label: "SubTotal", width: 80 },
  { key: "Total", label: "Total", width: 80 },
  { key: "Message", label: "Message", width: 140 },
  { key: "AVS", label: "AVS", width: 50 },
  { key: "Bill To", label: "Bill To", width: 130 },
  { key: "Billing Address", label: "Billing Address", width: 150 },
  { key: "Billing City", label: "Billing City", width: 100 },
  { key: "Billing State", label: "Billing State", width: 60 },
  { key: "Billing Zip", label: "Billing Zip", width: 70 },
  { key: "Telephone", label: "Telephone", width: 110 },
  { key: "Email Address", label: "Email Address", width: 160 },
  { key: "Notes/Memo", label: "Notes/Memo", width: 120 },
];

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const PayaReviewPanel = () => {
  const { environment } = useEnvironment();

  // File state
  const [fileName, setFileName] = useState(null);
  const [processedDate, setProcessedDate] = useState(null);
  const [allRows, setAllRows] = useState([]);

  // Processed data
  const [sonwRows, setSonwRows] = useState([]);
  const [flaggedClusters, setFlaggedClusters] = useState([]);
  const [duplicateClusters, setDuplicateClusters] = useState([]);
  const [cleanRows, setCleanRows] = useState([]);

  // UI state
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRow, setDialogRow] = useState(null);
  const [dialogNotes, setDialogNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Track which rows have been added
  const [addedRows, setAddedRows] = useState(new Set());

  // ═══════════════════════════════════════════════════════════
  // FILE IMPORT
  // ═══════════════════════════════════════════════════════════

  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseCSV(text);

      setFileName(file.name);
      setProcessedDate(new Date());
      setAllRows(parsed);

      // Filter to SONW orders
      const sonw = parsed.filter(
        (r) => r.OrderID && r.OrderID.trim().startsWith("SONW")
      );
      setSonwRows(sonw);

      // Build flagged clusters (Tab 2)
      const { clusters: fc, flaggedIndices } = buildFlaggedClusters(sonw);
      setFlaggedClusters(fc);

      // Build duplicate clusters (Tab 3)
      const { clusters: dc, dupIndices } = buildDuplicateClusters(
        sonw,
        flaggedIndices
      );
      setDuplicateClusters(dc);

      // Clean approvals (Tab 4) — not in flagged or duplicate
      const allExcluded = new Set([...flaggedIndices, ...dupIndices]);
      const clean = sonw.filter((_, i) => !allExcluded.has(i));
      setCleanRows(clean);

      setActiveTab(0);
      setPage(0);
      setAddedRows(new Set());
    };
    reader.readAsText(file);

    // Reset the input so re-importing the same file works
    event.target.value = "";
  };

  // ═══════════════════════════════════════════════════════════
  // ADD TO FRAUD DB
  // ═══════════════════════════════════════════════════════════

  const handleOpenDialog = (row) => {
    setDialogRow(row);
    setDialogNotes("");
    setDialogOpen(true);
  };

  const handleConfirmAdd = async () => {
    if (!dialogRow || !dialogNotes.trim()) return;
    setSubmitting(true);

    try {
      const payload = {
        orderNumber: dialogRow.OrderID,
        customerName: dialogRow["Bill To"],
        billingAddress: dialogRow["Billing Address"],
        billingCity: dialogRow["Billing City"],
        billingState: dialogRow["Billing State"],
        billingZip: dialogRow["Billing Zip"],
        phone: dialogRow["Telephone"],
        email: dialogRow["Email Address"],
        reviewNotes: dialogNotes.trim(),
        ipAddress: "",
      };

      await apiClient.payaAddToDatabase(payload);

      // Mark as added
      const rowKey = `${dialogRow.OrderID}-${dialogRow["Email Address"]}-${dialogRow["Bill To"]}`;
      setAddedRows((prev) => new Set([...prev, rowKey]));

      setSnackbar({
        open: true,
        message: `${dialogRow.OrderID} added to fraud database`,
        severity: "success",
      });
      setDialogOpen(false);
    } catch (err) {
      const msg =
        err.message && err.message.includes("Valid email address is required")
          ? "Valid email address is required"
          : `Failed to add record: ${err.message}`;
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmBan = async () => {
    if (!dialogRow || !dialogNotes.trim()) return;
    setSubmitting(true);

    try {
      const payload = {
        orderNumber: dialogRow.OrderID,
        customerName: dialogRow["Bill To"],
        billingAddress: dialogRow["Billing Address"],
        billingCity: dialogRow["Billing City"],
        billingState: dialogRow["Billing State"],
        billingZip: dialogRow["Billing Zip"],
        phone: dialogRow["Telephone"],
        email: dialogRow["Email Address"],
        reviewNotes: dialogNotes.trim(),
        ipAddress: "",
        targetSheet: "Chargebacks_v2",
      };

      await apiClient.payaAddToDatabase(payload);

      // Mark as added
      const rowKey = `${dialogRow.OrderID}-${dialogRow["Email Address"]}-${dialogRow["Bill To"]}`;
      setAddedRows((prev) => new Set([...prev, rowKey]));

      setSnackbar({
        open: true,
        message: `${dialogRow.OrderID} banned and added to chargebacks`,
        severity: "success",
      });
      setDialogOpen(false);
    } catch (err) {
      const msg =
        err.message && err.message.includes("Valid email address is required")
          ? "Valid email address is required"
          : `Failed to ban record: ${err.message}`;
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const getRowKey = (row) =>
    `${row.OrderID}-${row["Email Address"]}-${row["Bill To"]}`;

  // ═══════════════════════════════════════════════════════════
  // STATS
  // ═══════════════════════════════════════════════════════════

  const stats = useMemo(() => {
    const approvedInFlagged = flaggedClusters
      .flat()
      .filter((r) => !isNonApproved(r)).length;
    return {
      totalSONW: sonwRows.length,
      flaggedClusterCount: flaggedClusters.length,
      ordersAtRisk: approvedInFlagged,
      duplicateClusterCount: duplicateClusters.length,
      cleanCount: cleanRows.length,
    };
  }, [sonwRows, flaggedClusters, duplicateClusters, cleanRows]);

  // ═══════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════

  const renderActionCell = (row) => {
    const key = getRowKey(row);
    if (addedRows.has(key)) {
      return (
        <Button size="small" disabled startIcon={<CheckCircleIcon />}>
          Added
        </Button>
      );
    }
    return (
      <Button
        size="small"
        variant="outlined"
        color="error"
        onClick={() => handleOpenDialog(row)}
      >
        Add to Fraud Warnings
      </Button>
    );
  };

  const renderRow = (row, bgColor, idx) => (
    <TableRow key={idx} sx={{ backgroundColor: bgColor }}>
      {DISPLAY_COLUMNS.map((col) => (
        <TableCell
          key={col.key}
          sx={{ fontSize: "0.75rem", padding: "4px 5px", lineHeight: 1.2, whiteSpace: "nowrap" }}
        >
          {row[col.key] || ""}
        </TableCell>
      ))}
      <TableCell sx={{ padding: "4px 5px" }}>{renderActionCell(row)}</TableCell>
    </TableRow>
  );

  const renderClusterDivider = (clusterNum, count) => (
    <TableRow key={`divider-${clusterNum}`}>
      <TableCell
        colSpan={DISPLAY_COLUMNS.length + 1}
        sx={{
          backgroundColor: "#ECEFF1",
          padding: "4px 5px",
          fontWeight: 600,
          fontSize: "0.8rem",
          borderTop: "2px solid #B0BEC5",
        }}
      >
        Cluster {clusterNum} — {count} row{count !== 1 ? "s" : ""}
      </TableCell>
    </TableRow>
  );

  const renderTableHeader = () => (
    <TableHead>
      <TableRow>
        {DISPLAY_COLUMNS.map((col) => (
          <TableCell
            key={col.key}
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              whiteSpace: "nowrap",
              backgroundColor: "#F5F5F5",
              padding: "4px 5px",
            }}
          >
            {col.label}
          </TableCell>
        ))}
        <TableCell
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            backgroundColor: "#F5F5F5",
            padding: "4px 5px",
          }}
        >
          Actions
        </TableCell>
      </TableRow>
    </TableHead>
  );

  // ═══════════════════════════════════════════════════════════
  // TAB CONTENT RENDERERS
  // ═══════════════════════════════════════════════════════════

  const renderFlaggedClusters = () => {
    const allRowsFlat = [];
    flaggedClusters.forEach((cluster, ci) => {
      allRowsFlat.push({ type: "divider", clusterNum: ci + 1, count: cluster.length });
      cluster.forEach((row) => {
        const bg = isNonApproved(row) ? "#FFF9C4" : "#FCE4EC";
        allRowsFlat.push({ type: "row", row, bg });
      });
    });

    const paginated = allRowsFlat.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <>
        <TableContainer>
          <Table size="small">
            {renderTableHeader()}
            <TableBody>
              {paginated.map((item, idx) =>
                item.type === "divider"
                  ? renderClusterDivider(item.clusterNum, item.count)
                  : renderRow(item.row, item.bg, idx)
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={allRowsFlat.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </>
    );
  };

  const renderDuplicateClusters = () => {
    const allRowsFlat = [];
    duplicateClusters.forEach((cluster, ci) => {
      allRowsFlat.push({ type: "divider", clusterNum: ci + 1, count: cluster.length });
      cluster.forEach((row) => {
        allRowsFlat.push({ type: "row", row, bg: "#FFF3E0" });
      });
    });

    const paginated = allRowsFlat.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <>
        <TableContainer>
          <Table size="small">
            {renderTableHeader()}
            <TableBody>
              {paginated.map((item, idx) =>
                item.type === "divider"
                  ? renderClusterDivider(item.clusterNum, item.count)
                  : renderRow(item.row, item.bg, idx)
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={allRowsFlat.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </>
    );
  };

  const renderCleanApprovals = () => {
    const paginated = cleanRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <>
        <TableContainer>
          <Table size="small">
            {renderTableHeader()}
            <TableBody>
              {paginated.map((row, idx) =>
                renderRow(row, idx % 2 === 0 ? "#FFFFFF" : "#FAFAFA", idx)
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={cleanRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════

  return (
    <Paper id="paya-review-panel" sx={{ p: 3, mb: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" component="h2">
          Paya Transaction Review
          {environment === "staging" && (
            <Chip
              label="STAGING"
              size="small"
              color="warning"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadFileIcon />}
          size="small"
        >
          {fileName ? "Import New File" : "Import CSV"}
          <input
            type="file"
            accept=".csv"
            hidden
            onChange={handleFileImport}
          />
        </Button>
      </Box>

      {/* Empty state */}
      {!fileName && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={8}
        >
          <UploadFileIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Import a Paya transaction CSV to begin review
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
            The file is parsed locally — nothing is uploaded to any server
          </Typography>
        </Box>
      )}

      {/* File loaded state */}
      {fileName && (
        <>
          {/* File info */}
          <Box mb={1}>
            <Typography variant="body2" color="text.secondary">
              File: <strong>{fileName}</strong> — Processed:{" "}
              {processedDate?.toLocaleString()} — {allRows.length} total rows,{" "}
              {sonwRows.length} SONW orders
            </Typography>
          </Box>

          {/* Stats bar */}
          <Box display="flex" gap={1.5} flexWrap="wrap" mb={2}>
            <Chip
              label={`${stats.totalSONW} SONW transactions`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${stats.flaggedClusterCount} flagged clusters`}
              size="small"
              color="error"
              variant="outlined"
            />
            <Chip
              label={`${stats.ordersAtRisk} orders at risk`}
              size="small"
              color="error"
            />
            <Chip
              label={`${stats.duplicateClusterCount} duplicate clusters`}
              size="small"
              color="warning"
              variant="outlined"
            />
            <Chip
              label={`${stats.cleanCount} clean approvals`}
              size="small"
              color="success"
              variant="outlined"
            />
          </Box>

          <Divider sx={{ mb: 1 }} />

          {/* Sub-tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => {
              setActiveTab(v);
              setPage(0);
            }}
            sx={{ mb: 2 }}
          >
            <Tab
              label={`Flagged Clusters (${stats.flaggedClusterCount})`}
            />
            <Tab
              label={`Duplicate Clusters (${stats.duplicateClusterCount})`}
            />
            <Tab label={`Clean Approvals (${stats.cleanCount})`} />
          </Tabs>

          {activeTab === 0 && renderFlaggedClusters()}
          {activeTab === 1 && renderDuplicateClusters()}
          {activeTab === 2 && renderCleanApprovals()}
        </>
      )}

      {/* ═══════════════════════════════════════════════════════
          ADD TO FRAUD DB DIALOG
          ═══════════════════════════════════════════════════════ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add to Fraud Database</DialogTitle>
        <DialogContent>
          {dialogRow && (
            <Box sx={{ mt: 1 }}>
              <TextField
                label="Name"
                value={dialogRow["Bill To"] || ""}
                fullWidth
                size="small"
                margin="dense"
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Email"
                value={dialogRow["Email Address"] || ""}
                fullWidth
                size="small"
                margin="dense"
                InputProps={{ readOnly: true }}
              />
              {!dialogRow["Email Address"] && (
                <Alert severity="warning" sx={{ my: 1 }}>
                  No email — this record will require manual matching
                </Alert>
              )}
              <TextField
                label="Address"
                value={dialogRow["Billing Address"] || ""}
                fullWidth
                size="small"
                margin="dense"
                InputProps={{ readOnly: true }}
              />
              <Box display="flex" gap={1}>
                <TextField
                  label="City"
                  value={dialogRow["Billing City"] || ""}
                  size="small"
                  margin="dense"
                  InputProps={{ readOnly: true }}
                  sx={{ flex: 2 }}
                />
                <TextField
                  label="State"
                  value={dialogRow["Billing State"] || ""}
                  size="small"
                  margin="dense"
                  InputProps={{ readOnly: true }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Zip"
                  value={dialogRow["Billing Zip"] || ""}
                  size="small"
                  margin="dense"
                  InputProps={{ readOnly: true }}
                  sx={{ flex: 1 }}
                />
              </Box>
              <TextField
                label="Phone"
                value={dialogRow["Telephone"] || ""}
                fullWidth
                size="small"
                margin="dense"
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Order ID"
                value={dialogRow["OrderID"] || ""}
                fullWidth
                size="small"
                margin="dense"
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Notes (required)"
                value={dialogNotes}
                onChange={(e) => setDialogNotes(e.target.value)}
                fullWidth
                size="small"
                margin="dense"
                multiline
                rows={4}
                required
                placeholder="Enter reason for adding to fraud database..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmAdd}
            disabled={!dialogNotes.trim() || submitting}
          >
            {submitting ? "Adding..." : "Add to Fraud Warnings"}
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#b71c1c",
              "&:hover": {
                backgroundColor: "#8e0000",
              },
            }}
            onClick={handleConfirmBan}
            disabled={!dialogNotes.trim() || submitting}
          >
            {submitting ? "Adding..." : "Ban"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PayaReviewPanel;
